import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ContentStatus, SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

export function slugify(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// ── Chapter mutations ──────────────────────────────────────────────────────────

export type CreateChapterInput = {
  subject: SubjectSlug;
  level: SchoolLevel;
  title: string;
  slug: string;
  description?: string | null;
  display_order: number;
  status: ContentStatus;
};

export function useCreateChapter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateChapterInput) => {
      const { data, error } = await supabase
        .from('chapters')
        .insert({ ...input, description: input.description ?? null })
        .select('id')
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-chapters'] });
    },
  });
}

export type UpdateChapterInput = {
  id: string;
  title?: string;
  slug?: string;
  description?: string | null;
  display_order?: number;
  status?: ContentStatus;
  subject?: SubjectSlug;
  level?: SchoolLevel;
};

export function useUpdateChapter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: UpdateChapterInput) => {
      const { error } = await supabase.from('chapters').update(patch).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin-chapters'] });
      qc.invalidateQueries({ queryKey: ['admin-chapter-detail', variables.id] });
    },
  });
}

// ── Lesson mutations ───────────────────────────────────────────────────────────

export type CreateLessonInput = {
  chapter_id: string;
  title: string;
  slug: string;
  display_order: number;
  status?: ContentStatus;
};

export function useCreateLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateLessonInput) => {
      const { data, error } = await supabase
        .from('lessons')
        .insert({ ...input, content_md: '', video_url: null, status: input.status ?? 'draft' })
        .select('id')
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin-chapter-detail', variables.chapter_id] });
      qc.invalidateQueries({ queryKey: ['admin-chapters'] });
    },
  });
}

export type UpdateLessonInput = {
  id: string;
  chapter_id?: string;
  title?: string;
  slug?: string;
  content_md?: string;
  video_url?: string | null;
  display_order?: number;
  status?: ContentStatus;
};

export function useUpdateLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: UpdateLessonInput) => {
      const { error } = await supabase.from('lessons').update(patch).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin-lesson-detail', variables.id] });
      if (variables.chapter_id) {
        qc.invalidateQueries({ queryKey: ['admin-chapter-detail', variables.chapter_id] });
      }
    },
  });
}

export type DeleteLessonInput = {
  id: string;
  chapter_id: string;
};

export function useDeleteLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DeleteLessonInput) => {
      const { error } = await supabase.from('lessons').delete().eq('id', input.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin-chapter-detail', variables.chapter_id] });
      qc.invalidateQueries({ queryKey: ['admin-chapters'] });
    },
  });
}

// ── Competence mutations ───────────────────────────────────────────────────────

export type CreateCompetenceInput = {
  chapter_id: string;
  title: string;
  slug: string;
  display_order: number;
};

export function useCreateCompetence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCompetenceInput) => {
      const { data, error } = await supabase
        .from('competences')
        .insert({ ...input, tags: [], prerequis: [], erreurs_classiques: [] })
        .select('id')
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin-chapter-detail', variables.chapter_id] });
      qc.invalidateQueries({ queryKey: ['admin-chapters'] });
    },
  });
}

export type UpdateCompetenceInput = {
  id: string;
  chapter_id?: string;
  title?: string;
  slug?: string;
  display_order?: number;
  tags?: string[];
  prerequis?: string[];
  erreurs_classiques?: string[];
};

export function useUpdateCompetence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, chapter_id: _chapterId, ...patch }: UpdateCompetenceInput) => {
      const { error } = await supabase.from('competences').update(patch).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin-competence-detail', variables.id] });
      if (variables.chapter_id) {
        qc.invalidateQueries({ queryKey: ['admin-chapter-detail', variables.chapter_id] });
      }
    },
  });
}

export type DeleteCompetenceInput = {
  id: string;
  chapter_id: string;
};

export function useDeleteCompetence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DeleteCompetenceInput) => {
      const { error } = await supabase.from('competences').delete().eq('id', input.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin-chapter-detail', variables.chapter_id] });
      qc.invalidateQueries({ queryKey: ['admin-chapters'] });
    },
  });
}
